import React, { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { BrandingProvider, useBranding } from './context/BrandingContext';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './layouts/home';
import Login from './layouts/authentication/Login';
import SignUp from './layouts/authentication/SignUp';
import ForgotPassword from './layouts/authentication/ForgotPassword';
import ResetPassword from './layouts/authentication/ResetPassword';
import ClientForm from './layouts/client-form';
import Dashboard from './layouts/Dashboard';
import {
  getContrastText,
  normalizeHex,
  resolveActionPrimary,
} from './utils/theme-colors.util';

/**
 * Componente interno que aplica o tema dinâmico baseado nas configurações de branding
 */
const ThemedApp: React.FC = () => {
  const { branding, isLoading } = useBranding();

  // Cria o tema dinamicamente com base nas cores do branding
  const theme = useMemo(() => {
    const brandPrimary =
      normalizeHex(branding.colors.primary) || branding.colors.primary;
    const actionPrimary = resolveActionPrimary({
      primary: branding.colors.primary,
      primaryFocus: branding.colors.primaryFocus,
      sidebarStart: branding.colors.sidebarStart,
    });
    const actionContrastText = getContrastText(actionPrimary);

    return createTheme({
      palette: {
        primary: {
          main: actionPrimary,
          dark: branding.colors.primaryFocus,
          light: brandPrimary,
          contrastText: actionContrastText,
        },
        secondary: {
          main: branding.colors.secondary,
        },
        background: {
          default: branding.colors.background,
        },
        text: {
          primary: branding.colors.textDark,
        },
      },
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      },
      components: {
        MuiAppBar: {
          styleOverrides: {
            root: {
              background: branding.colors.headerBackground
                ? branding.colors.headerBackground
                : `linear-gradient(to right, ${branding.colors.sidebarStart}, ${branding.colors.sidebarEnd})`,
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: {
              backgroundColor: branding.colors.sidebarStart,
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              '&.Mui-selected': {
                color: branding.colors.sidebarStart,
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            containedPrimary: {
              color: actionContrastText,
            },
            outlinedPrimary: {
              borderColor: actionPrimary,
              color: actionPrimary,
              '&:hover': {
                borderColor: branding.colors.primaryFocus,
                backgroundColor: alpha(actionPrimary, 0.06),
              },
            },
            textPrimary: {
              color: actionPrimary,
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            colorPrimary: {
              color: actionPrimary,
              '&:hover': {
                backgroundColor: alpha(actionPrimary, 0.08),
              },
            },
          },
        },
      },
    });
  }, [branding]);

  // Mostra loading enquanto carrega as configurações de branding
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/r/:affiliateCode" element={<ClientForm />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </ThemeProvider>
  );
};

/**
 * Componente principal da aplicação
 * Envolve tudo com o BrandingProvider para disponibilizar as configurações de branding
 */
const App: React.FC = () => {
  return (
    <BrandingProvider>
      <ThemedApp />
    </BrandingProvider>
  );
};

export default App;
